/* eslint-disable sonarjs/no-identical-functions */
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import {
  ELSPropsFromModalService,
  ELSWithModalService,
} from '@els/els-component-modal-react';
import { ELSURLHelper, } from '@els/els-ui-common-react';
import { uniqBy, flowRight } from 'lodash';
import moment from 'moment';
import axios from 'axios';
import {
  Link,
  useHistory,
  useLocation,
} from 'react-router-dom';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';
import withPageLoader from '../../hocs/with-page-loader/withPageLoader.hoc';
import {
  ChatContextTypeDto,
  ChatDto,
  ChatEntryAuthorDto,
  ChatEntryDto,
  ChatEntryEvaluationDto,
  ChatEntryStatus,
  ChatEntryTypeDto,
  ChatReferenceDto,
  ChatReferenceWithParsedBookContentProps,
  FollowUpQuestionDto,
  PostProcessJobDto,
  PostProcessJobStatusDto,
  PostProcessJobTypeDto,
  PromptOverrideDto,
  RelatedContentDto,
  SearchOverrideDto,
  ShellsDto,
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FEATURE_FLAG } from '../../apis/eols-features-api/eols-features-api.constants';
import { ServerConstants } from '../../components/app/server.constants';
import {
  ADMIN_MODAL_ID,
  ChatConnectionProtocol,
  ChatModeType,
  IframeEvent,
  osmosisVideoUrl,
  pageSize,
} from './ai-chat.constants';
import {
  buildTempUserEntry,
  getActiveChat,
  getAdminOverrideFromStorage,
  getBrand,
  getBrandName,
  getChatContextForNewChat,
  getChatEntryCount,
  getCitations,
  getDefaultActiveChat,
  getEntryEvaluation,
  getLastEntry,
  getNewDefaultChat,
  getParentEntry,
  getParsedContentPropsBookChunk,
  getSelectedIsbnFilters,
  getSortedChatEntries,
  getUserName,
  handleChatScroll,
  isAdminUser,
  getPostProcessJob,
  isAllowedMessageOrigin,
  isIframeContext,
  isInputBlocked,
  isFilterRelevantReferencesJobPending,
  isPostProcessJobInProgress,
  isPostProcessJobTerminal,
  pollPostProcessJobs,
  putAdminOverrideInStorage,
  resolveEntriesAfterPostProcess,
  resolveRequestedChatMode,
  toastActiveStreamingError,
  buildCksUrlFromCitation,
  buildEbookCfiSsoUrlRequestFromCitation,
  buildEbookSsoUrlRequestFromCitation,
  openEbookAtCfiOrPageRange,
  isCitationClickable,
  isCKSLink,
  isVSTChat,
  getStartPageNumber,
  getEndPageNumber,
  toastApiError,
} from './ai-chat.utilities';
import { RoutePath } from '../../components/app/app.constants';
import {
  getDefaultRequestOptions
} from '../../utilities/api.utilities';
import AiChatAdminModal from './AiChatAdminModal.component';
import { EntrySubmitProps } from './ai-chat.models';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  fetchChatEntriesAction,
  fetchChatEntryAction,
  fetchChatEvaluationsAction,
  fetchFollowUpQuestionsAction,
  fetchPostProcessJobsAction,
  fetchRelatedContentAction,
  handleChatCreateAction,
  handleChatUpdateAction,
  patchFollowUpQuestionAction,
  postChatEntryShellsAction,
  trackAdobeAction,
  trackNewRelicAction
} from '../../components/app/app-state-actions.utilities';
import {
  isFeatureFlagEnabled
} from '../../utilities/featureFlag.utilities';
import AiChatEntry from './AiChatEntry.component';
import {
  deleteSearchParams,
  getObjectFromSearchParams,
} from '../../utilities/app.utilities';
import { emptyObject } from '../../models/els.models';
import { stripNilProps } from '../../utilities/common.utilities';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import {
  AdobeAnalyticsAction,
  AdobeAnalyticsContent,
  AdobeAnalyticsPageData
} from '../../models/adobe-analytics.models';
import Panel from '../../components/panel/Panel.component';
import { QuizPanelMode } from '../../components/quiz-questions-panel/quiz-questions-panel.constants';
import AiChatBaseTemplate from '../../components/ai-chat-base-template/AiChatBaseTemplate.component';
import { Brand } from '../../constants/common.constants';
import { AiChatInputBar } from './AiChatInputBar.component';

export enum ScreenReaderAnnouncementStatus {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR'
}

export type AiChatProps = ELSPropsFromToastService & ELSPropsFromModalService;

export type AiChatState = {
  chatEntries: ChatEntryDto[];
  // Display-only copy of the user's own message, rendered immediately on submit and cleared
  // once the persisted entries arrive (or the submit fails). Never part of chatEntries.
  tempUserEntry: ChatEntryDto;
  totalPages: number;
  currentPage: number;
  chatEvaluations: ChatEntryEvaluationDto[];
  prompt: string;
  screenReaderAnnouncementStatus: ScreenReaderAnnouncementStatus;
  submitStart: string;
  streamStart: string;
  firstTokenReceived: string;
  promptOverride: PromptOverrideDto;
  searchOverride: SearchOverrideDto;
  isAutoScrolling: boolean;
  followUpQuestions: FollowUpQuestionDto[];
  // Bot entry ids whose follow-up question fetch has resolved (even if it returned
  // none). Used to stop the loading indicator once a fetch completes — an entry can
  // legitimately have zero follow-up questions (e.g. citation-free responses).
  followUpResolvedEntryIds: Set<number>;
  scrollToChatBottom: boolean;
  appLinkQuerySubmitted: boolean;
  selectedChatReference: ChatReferenceDto;
  selectedRelatedContent: RelatedContentDto;
  selectedFlashcardEntryId: number;
  selectedQuizQuestionEntryId: number;
  selectedQuizQuestionMode: QuizPanelMode;
  selectedCitationsEntryId: number;
  // Raw citation `index` to scroll to within the References panel once it opens;
  // null when the panel was opened generically (e.g. the "References (N)" link)
  // rather than from a specific inline citation pill.
  selectedCitationIndex: number;
  initiallyLoadedEntryIds: Set<number>;
  adminOverridePageContext: string;
}

const defaultState: AiChatState = {
  chatEntries: null,
  tempUserEntry: null,
  totalPages: null,
  currentPage: null,
  chatEvaluations: null,
  prompt: '',
  screenReaderAnnouncementStatus: ScreenReaderAnnouncementStatus.IDLE,
  streamStart: null,
  submitStart: null,
  firstTokenReceived: null,
  promptOverride: null,
  isAutoScrolling: true,
  searchOverride: null,
  followUpQuestions: null,
  followUpResolvedEntryIds: new Set(),
  scrollToChatBottom: false,
  appLinkQuerySubmitted: false,
  selectedChatReference: null,
  selectedRelatedContent: null,
  selectedFlashcardEntryId: null,
  selectedQuizQuestionEntryId: null,
  selectedQuizQuestionMode: null,
  selectedCitationsEntryId: null,
  selectedCitationIndex: null,
  initiallyLoadedEntryIds: new Set(),
  adminOverridePageContext: '',
};

export const AiChatComponent = (props: AiChatProps): JSX.Element => {

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);

  const [state, setState] = useState<AiChatState>(defaultState);
  const screenReaderAnnouncementTimeout = useRef<NodeJS.Timeout | null>(null);

  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const handleOsmosisEvent = (e: MessageEvent) => {
      if (e.origin !== osmosisVideoUrl) {
        return;
      }

      if (!e.data || !e.data.type) {
        return;
      }

      if (['announce-frame-initialized', 'video_start', 'video_finished'].includes(e.data.type)) {
        const { activeChatId } = appState;
        trackNewRelicAction({
          appStateRef,
          actionProps: {
            action: NewRelicAnalyticsAction.OSMOSIS_VIDEO_PLAYER_EVENT,
            props: {
              type: e.data.type,
              activeChatId: activeChatId ? activeChatId.toString() : null,
              videoId: e.data.url,
            },
          },
        });
      }

    };

    window.addEventListener('message', handleOsmosisEvent, false);

    return () => {
      window.removeEventListener('message', handleOsmosisEvent, false);
    };
  }, [appState.activeChatId, appState]);

  useEffect(() => {
    const handlePageContextUpdate = (e: MessageEvent) => {
      if (!isAllowedMessageOrigin(e.origin)) {
        return;
      }

      if (!e.data || e.data.type !== IframeEvent.SHERPATH_AI_PAGE_CONTEXT_UPDATE) {
        return;
      }

      const { pageContext } = e.data;
      if (typeof pageContext === 'string') {
        setAppState((prevState) => ({
          ...prevState,
          pageContext
        }));
      }
    };

    window.addEventListener('message', handlePageContextUpdate, false);

    // Signal to parent window that iframe is ready to receive pageContext updates
    if (window.parent) {
      window.parent.postMessage({ type: IframeEvent.SHERPATH_AI_IFRAME_READY }, '*');
    }

    return () => {
      window.removeEventListener('message', handlePageContextUpdate, false);
    };
  }, [setAppState]);

  useEffect(() => {
    if (state.scrollToChatBottom) {
      handleChatScroll(state);
      setState((prevState) => {
        return {
          ...prevState,
          scrollToChatBottom: false
        };
      });
    }

  }, [state.scrollToChatBottom]);

  useEffect(() => {

    if (!appState.activeChatId) {
      // Clear entries when no chat is active (e.g., last chat was deleted)
      setState((prevState) => {
        return {
          ...prevState,
          chatEntries: null,
          totalPages: null,
          currentPage: null,
          chatEvaluations: null,
          followUpQuestions: null,
          followUpResolvedEntryIds: new Set(),
          selectedChatReference: null,
          selectedRelatedContent: null,
          selectedFlashcardEntryId: null,
          selectedQuizQuestionEntryId: null,
          selectedQuizQuestionMode: null,
          selectedCitationsEntryId: null,
          initiallyLoadedEntryIds: new Set(),
        };
      });
      return;
    }

    // Captured so the async callbacks below can verify the active chat has not changed
    // (e.g. via the landOnNewChat effect clearing it) before this history load resolves --
    // otherwise a stale response could still commit entries for a chat that is no longer active.
    const requestedChatId = appState.activeChatId;
    const isStillActiveChat = () => appStateRef.current.activeChatId === requestedChatId;

    fetchChatEvaluationsAction({
      appState,
      appStateRef,
      setAppState,
      chatId: appState.activeChatId
    }).then((response) => {
      if (!isStillActiveChat()) {
        return;
      }
      setState((prevState) => {
        return {
          ...prevState,
          chatEvaluations: response,
        };
      });
    });
    fetchChatEntriesAction({
      appState,
      appStateRef,
      setAppState,
      chatId: appState.activeChatId,
      page: 0,
      pageSize
    }).then((response) => {
      if (!isStillActiveChat()) {
        return;
      }
      // A brand-new chat's id is set (and this effect fires) before the submit chain has saved its
      // first entry, so this fetch can race that save: isStillActiveChat() alone can't tell the two
      // apart, since the id does not change either way. appStateRef.current.isLoading covers exactly
      // that submit-in-flight window, so a response landing while it's true is stale here -- drop it
      // and let the submit chain's own optimistic/saved entries remain the source of truth.
      if (appStateRef.current.isLoading) {
        return;
      }
      const lastEntry = getLastEntry(response.content);
      if (lastEntry) {
        fetchFollowUpQuestionsAction({
          appState,
          appStateRef,
          setAppState,
          botEntryId: lastEntry.id,
        }).then((followUpQuestions) => {
          if (!isStillActiveChat()) {
            return;
          }
          setState((prevState) => {
            return {
              ...prevState,
              followUpQuestions,
              followUpResolvedEntryIds: new Set(prevState.followUpResolvedEntryIds).add(lastEntry.id),
              scrollToChatBottom: true,
            };
          });
        });
      }
      setState((prevState) => {
        return {
          ...prevState,
          chatEntries: response.content,
          totalPages: response.totalPages,
          currentPage: response.currentPage,
          scrollToChatBottom: true,
          selectedChatReference: null,
          selectedRelatedContent: null,
          selectedFlashcardEntryId: null,
          selectedQuizQuestionEntryId: null,
          selectedQuizQuestionMode: null,
          selectedCitationsEntryId: null,
          prompt: '',
          initiallyLoadedEntryIds: new Set(response.content.map((entry) => entry.id))
        };
      });
    }).finally(() => {
      if (!isStillActiveChat()) {
        return;
      }
      setAppState((prevState) => {
        return {
          ...prevState,
          showLoader: false,
        };
      })
    });

  }, [appState.activeChatId]);

  const updateChatOnLaterPrompt = (newEntry: ChatEntryDto, chat: ChatDto): Promise<ChatDto> => {
    const lastAccessed = new Date().toISOString();
    setAppState((prevState) => {
      return {
        ...prevState,
        chats: prevState.chats.map((_chat) => {
          if (_chat.id !== chat.id) {
            return _chat;
          }
          return {
            ..._chat,
            lastAccessed
          }
        })
      }
    })
    return Promise.resolve({
      ...chat,
      lastAccessed
    })
  };

  // wasCreated tells the caller whether `chat` was just created with the real title (see
  // getNewDefaultChat below) or is a reused active chat -- entry.index alone cannot distinguish
  // these, since a reused chat with no persisted entries (e.g. an empty "New chat" left by an
  // older session) also produces index 0 without ever having its title set.
  const getActiveOrCreateChat = (isNewChat: boolean, prompt: string): Promise<{ chat: ChatDto; wasCreated: boolean }> => {
    if (isNewChat) {
      return handleChatCreateAction({
        appState,
        appStateRef,
        setAppState,
        chat: getNewDefaultChat(appState, appState.chatUserRole, prompt.substring(0, 1000))
      }).then((chat) => ({ chat, wasCreated: true }));
    }
    const activeChat = getActiveChat(appState);
    if (activeChat) {
      if (!activeChat.ecrId && appState.ecrId) {
        return handleChatUpdateAction({
          appState,
          appStateRef,
          setAppState,
          chat: {
            ...activeChat,
            ecrId: appState.ecrId
          }
        }).then((chat) => ({ chat, wasCreated: false }));
      }
      return Promise.resolve({ chat: activeChat, wasCreated: false });
    }
    return handleChatCreateAction({
      appState,
      appStateRef,
      setAppState,
      chat: getNewDefaultChat(appState, appState.chatUserRole, prompt.substring(0, 1000))
    }).then((chat) => ({ chat, wasCreated: true }));
  };

  const isAdminOverrideEnabled = () => {
    return isAdminUser() && appState.enableAdminOverride;
  };

  const handleHttpStreamChunk = (message: string, botEntry: ChatEntryDto) => {
    setState((prevState) => {
      return {
        ...prevState,
        firstTokenReceived: prevState.firstTokenReceived || moment().toISOString(),
        chatEntries: prevState.chatEntries.map((entry) => {
          if (botEntry.id === entry.id) {
            const newBotEntry = { ...entry };
            newBotEntry.message = message;
            newBotEntry._status = ChatEntryStatus.STREAMING;
            return newBotEntry;

          }
          return entry;
        })
      };
    });
  };

  const httpStream = (entry: ChatEntryDto, botEntry: ChatEntryDto): Promise<string> => {

    const postBody = {
      userEntryId: entry.id,
      botEntryId: botEntry.id,
      isOsmosis: getBrand(appState) === Brand.OSMOSIS_AI,
    };

    if (isFeatureFlag(FEATURE_FLAG.DISABLE_IS_OSMOSIS_BOOLEAN)) {
      delete postBody.isOsmosis;
    }

    const url = `${ServerConstants[ServerConstants.DataSource].nacsBaseUrl}/nacs/prompt/stream`

    const requestOptions = getDefaultRequestOptions({}, {});
    // Convert useWithCredentials to withCredentials for direct axios calls
    const { useWithCredentials, ...axiosOptions } = requestOptions;

    // axios 1.x throttles onDownloadProgress and, on the trailing-edge tick,
    // invokes it with a *synthetic* progress object that has no `.event` (and so
    // no XHR to read `responseText` from). Relying on `progressEvent.event`
    // therefore drops most chunks, leaving the response to appear all at once.
    // Capture the XHR the first time a native event carries it, then read the
    // accumulating `responseText` off it on every tick regardless of shape.
    let xhrRef: XMLHttpRequest;

    return axios({
      url,
      method: 'POST',
      data: postBody,
      withCredentials: useWithCredentials as boolean,
      onDownloadProgress: (progressEvent) => {
        const xhrFromEvent = progressEvent.event?.currentTarget as XMLHttpRequest;
        if (xhrFromEvent) {
          xhrRef = xhrFromEvent;
        }
        const responseText = xhrRef?.responseText;
        if (typeof responseText === 'string') {
          handleHttpStreamChunk(responseText, botEntry);
        }
      },
      ...axiosOptions
    }).then((response) => {
      return response.data;
    });
  };

  const saveAndSetShells = (userEntry: ShellsDto, chatId: number): Promise<ChatEntryDto[]> => {

    if (!userEntry.message) {
      throw new Error('no user message');
    }

    return postChatEntryShellsAction({
      appState,
      appStateRef,
      setAppState,
      entry: userEntry,
      chatId
    }).then((response) => {
      return new Promise((resolve) => {
        setState((prevState) => {
          const newEntries = response.map((entry) => {
            if (entry.author === ChatEntryAuthorDto.BOT) {
              return {
                ...entry,
                _status: ChatEntryStatus.THINKING,
              };
            }
            return entry;
          });

          if (prevState.chatEntries) {
            const newEntryIds = newEntries.map((entry) => entry.id);
            const filteredExistingEntries = prevState.chatEntries.filter((entry) => {
              return !newEntryIds.includes(entry.id);
            });
            return {
              ...prevState,
              chatEntries: [...filteredExistingEntries, ...newEntries],
              // The real entries have arrived, so the display-only copy is no longer needed --
              // clear it here rather than waiting for the submit chain's finally(), since the
              // remaining streaming leg can keep that chain open well after this point.
              tempUserEntry: null,
              scrollToChatBottom: true
            };
          }
          return {
            ...prevState,
            chatEntries: newEntries,
            tempUserEntry: null,
            scrollToChatBottom: true
          };
        });

        resolve(response);
      });
    });
  };

  const isFeatureFlag = (flag: FEATURE_FLAG): boolean => {
    return isFeatureFlagEnabled(flag, appState.featureFlagsGrouped, appState.courseSectionId);
  };

  // Starts a single polling coordinator that tracks every expected post-process
  // job for the entry with one unfiltered GET /post-process-jobs?entryId per tick,
  // upserting each job's status until all expected types reach a terminal state.
  const startPostProcessPolling = (
    botEntry: ChatEntryDto,
    chatMode: ChatModeType,
  ): void => {
    const isTutorModeFeatureFlagEnabled = isFeatureFlagEnabled(
      FEATURE_FLAG.ENABLE_TUTOR_MODE,
      appStateRef.current.featureFlagsGrouped,
      appStateRef.current.courseSectionId,
    );
    const isTutorMode = isTutorModeFeatureFlagEnabled
      && chatMode === ChatModeType.TUTOR_MODE;
    pollPostProcessJobs({
      botEntry,
      isTutorMode,
      appStateRef,
      setAppState,
      fetchJobsByEntry: (entryId) => fetchPostProcessJobsAction({
        appState,
        appStateRef,
        setAppState,
        entryId,
      }),
      upsertJob: upsertPostProcessJob,
    });
  };

  const handlePostProcessing = (
    botEntry: ChatEntryDto,
    attempt = 0,
    chatMode: ChatModeType,
  ): Promise<ChatEntryDto> => {

    if (attempt > 0) {
      throw new Error('NACS should solve for async issue so should never need to poll for updated entryType')
    }

    if (attempt >= 5) {
      trackNewRelicAction({
        appStateRef,
        actionProps: {
          action: NewRelicAnalyticsAction.AI_CHAT_ERROR,
          props: {
            type: 'POST_PROCESSING_ATTEMPTS_EXCEEDED',
            entryId: botEntry.id,
            chatId: botEntry.chatId,
          }
        }
      });
      return Promise.resolve(botEntry);
    }

    return fetchChatEntryAction({
      appState,
      setAppState,
      appStateRef,
      chatId: botEntry.chatId,
      entryId: botEntry.id
    }).then((response) => {
      if (response.entryType === ChatEntryTypeDto.SHELL) {
        return new Promise<ChatEntryDto>((resolve) => {
          setTimeout(() => {
            handlePostProcessing(response, attempt + 1, chatMode).then(resolve);
          }, 1000);
        });
      }

      startPostProcessPolling(response, chatMode);

      return Promise.resolve(response);
    });
  };

  const upsertPostProcessJob = (job: PostProcessJobDto): void => {
    setState((prevState) => {
      if (!prevState.chatEntries) {
        return prevState;
      }
      return {
        ...prevState,
        chatEntries: prevState.chatEntries.map((entry) => {
          if (entry.id !== job.entryId) {
            return entry;
          }
          const existing = entry.postProcessJobs || [];
          const matchIndex = existing.findIndex((e) => {
            if (job.id != null && e.id != null) {
              return e.id === job.id;
            }
            return e.jobType === job.jobType;
          });
          const nextJobs = matchIndex === -1
            ? [...existing, job]
            : existing.map((e, i) => (i === matchIndex ? { ...e, ...job } : e));
          return { ...entry, postProcessJobs: nextJobs };
        }),
      };
    });
  };

  // Tracks entry ids whose related-content fetch has already been triggered, so the
  // effect below reacts exactly once per entry regardless of how many times it re-runs
  // as postProcessJobs updates.
  const relatedContentFetchedEntryIds = useRef<Set<number>>(new Set());
  // Fetch related content once both the images and videos jobs for an entry have
  // reached a terminal state (COMPLETED, ERROR, or CANCELLED). Checking any terminal
  // status (not just COMPLETED) ensures the fetch still fires when one job errors
  // while the other succeeds.
  useEffect(() => {
    (state.chatEntries || []).forEach((entry) => {
      // History entries arrive with their jobs already resolved — only react to jobs
      // that transition live, via polling, in this session.
      if (state.initiallyLoadedEntryIds.has(entry.id)) {
        return;
      }
      if (relatedContentFetchedEntryIds.current.has(entry.id)) {
        return;
      }
      const imagesJob = getPostProcessJob(entry, PostProcessJobTypeDto.RETRIEVE_RELATED_CONTENT_IMAGES);
      const videosJob = getPostProcessJob(entry, PostProcessJobTypeDto.RETRIEVE_RELATED_CONTENT_VIDEOS);
      if (!imagesJob && !videosJob) {
        return;
      }
      if (isPostProcessJobTerminal(imagesJob) && isPostProcessJobTerminal(videosJob)) {
        relatedContentFetchedEntryIds.current.add(entry.id);
        fetchRelatedContentAction({
          appState,
          appStateRef,
          setAppState,
          botEntryId: entry.id,
          setChatState: setState,
        });
      }
    });
  }, [state.chatEntries, state.initiallyLoadedEntryIds]);

  // Tracks entry ids whose references have already been re-fetched, so the effect
  // below reacts exactly once per entry.
  const referencesRefetchedEntryIds = useRef<Set<number>>(new Set());
  // Re-fetch the bot entry once a job that resolves reference visibility transitions
  // to COMPLETED so the entry's references reflect the backend's visibility filtering.
  // FILTER_RELEVANT_REFERENCES does this for cited answers; GENERATE_FLASHCARDS and
  // GENERATE_QUIZ_QUESTIONS do the same (marking every CITATION reference used) once
  // generation succeeds.
  useEffect(() => {
    (state.chatEntries || []).forEach((entry) => {
      // History entries arrive with their jobs already resolved — only react to jobs
      // that transition live, via polling, in this session.
      if (state.initiallyLoadedEntryIds.has(entry.id)) {
        return;
      }
      if (referencesRefetchedEntryIds.current.has(entry.id)) {
        return;
      }
      const referenceVisibilityJob = (
        getPostProcessJob(entry, PostProcessJobTypeDto.FILTER_RELEVANT_REFERENCES)
        || getPostProcessJob(entry, PostProcessJobTypeDto.GENERATE_FLASHCARDS)
        || getPostProcessJob(entry, PostProcessJobTypeDto.GENERATE_QUIZ_QUESTIONS)
      );
      if (referenceVisibilityJob?.status !== PostProcessJobStatusDto.COMPLETED) {
        return;
      }
      referencesRefetchedEntryIds.current.add(entry.id);
      fetchChatEntryAction({
        appState,
        appStateRef,
        setAppState,
        chatId: referenceVisibilityJob.chatId,
        entryId: entry.id,
      }).then((refreshedEntry) => {
        setState((prevState) => ({
          ...prevState,
          chatEntries: (prevState.chatEntries || []).map((e) => {
            if (e.id !== refreshedEntry.id) {
              return e;
            }
            // Only adopt the refreshed references — preserve postProcessJobs and
            // other locally-merged fields to avoid races with concurrent pollers.
            return { ...e, references: refreshedEntry.references };
          }),
        }));
      });
    });
  }, [state.chatEntries, state.initiallyLoadedEntryIds]);

  // Tracks entry ids whose follow-up questions have already been fetched, so the
  // effect below reacts exactly once per entry.
  const followUpQuestionsFetchedEntryIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    (state.chatEntries || []).forEach((entry) => {
      // History entries arrive with their jobs already resolved — only react to jobs
      // that transition live, via polling, in this session.
      if (state.initiallyLoadedEntryIds.has(entry.id)) {
        return;
      }
      if (followUpQuestionsFetchedEntryIds.current.has(entry.id)) {
        return;
      }
      const followUpJob = getPostProcessJob(entry, PostProcessJobTypeDto.GENERATE_FOLLOW_UP_QUESTIONS);
      if (followUpJob?.status !== PostProcessJobStatusDto.COMPLETED) {
        return;
      }
      followUpQuestionsFetchedEntryIds.current.add(entry.id);
      fetchFollowUpQuestionsAction({
        appState,
        appStateRef,
        setAppState,
        botEntryId: entry.id,
      }).then((followUpQuestions: FollowUpQuestionDto[]) => {
        setState((prevState) => ({
          ...prevState,
          followUpQuestions,
          followUpResolvedEntryIds: new Set(prevState.followUpResolvedEntryIds).add(entry.id),
        }));
      });
    });
  }, [state.chatEntries, state.initiallyLoadedEntryIds]);

  const trackResponseComplete = (_props: {
    botEntry: ChatEntryDto;
    userEntry: ChatEntryDto;
  }) => {

    const {
      botEntry,
      userEntry,
    } = _props;

    setState((prevState) => {
      const {
        streamStart,
        submitStart,
        firstTokenReceived
      } = prevState;

      let millisecondsToComplete = null;
      let millisecondsToCompleteTotal = null;
      let millisecondsToFirstToken = null;

      if (streamStart && submitStart && firstTokenReceived) {
        millisecondsToComplete = moment().diff(streamStart, 'milliseconds');
        millisecondsToCompleteTotal = moment().diff(submitStart, 'milliseconds');
        millisecondsToFirstToken = moment(firstTokenReceived).diff(submitStart, 'milliseconds');
      }

      trackAdobeComplete({
        botEntry,
        userEntry,
        timeInMilli: millisecondsToCompleteTotal
      });

      trackNewRelicAction({
        appStateRef,
        actionProps: {
          action: NewRelicAnalyticsAction.AI_CHAT_BOT_RESPONSE_COMPLETE,
          props: {
            millisecondsToComplete,
            millisecondsToCompleteTotal,
            millisecondsToFirstToken,
            entryId: botEntry.id,
            idx: botEntry.index,
            isError: !!botEntry.errorMessage,
            chatUserRole: appState.chatUserRole,
          }
        }
      });

      return {
        ...prevState,
        streamStart: null,
        submitStart: null,
        firstTokenReceived: null
      };
    });
  };

  const getAdobeEventContentFromCitations = (botEntry: ChatEntryDto): AdobeAnalyticsContent[] => {
    const citations = getCitations(botEntry);
    if (!citations) {
      return [];
    }
    return citations.map((reference, idx): AdobeAnalyticsContent => {
      const parsedContentProps = getParsedContentPropsBookChunk(reference);
      return { // for books
        entitlementType: reference.type, // low-priority
        format: reference.type,
        id: reference.resourceId,
        type: parsedContentProps ? parsedContentProps.source_id_type : null, // "sd:answers:BK:summary:source",
        // "details": "in-main-summary=[true|false]&data-source=[abstract|full-text]",
        details: '',
        position: idx.toString(),
        publicationType: reference.resourceType,
        isbn: parsedContentProps ? parsedContentProps.source_id : null,
        provider: 'elsevier'
      };
    });
  };

  const trackAdobeComplete = (
    _props: {
      botEntry: ChatEntryDto;
      userEntry: ChatEntryDto;
      timeInMilli: number;
    }
  ) => {

    const {
      botEntry,
      userEntry,
      timeInMilli
    } = _props;

    const eventData: Partial<AdobeAnalyticsPageData> = {
      form: null,
      genAI: {
        conversationId: botEntry.chatId.toString(),
        promptCounter: getChatEntryCount(botEntry),
        input: userEntry.message,
        inputSource: 'user',
        inputOrigin: 'chat',
        answerDetails: '',
        citationsCount: botEntry.references?.length?.toString(),
        processingTime: timeInMilli ? timeInMilli.toString() : ''
      },
      content: getAdobeEventContentFromCitations(botEntry),
    };

    if (botEntry.errorMessage) {
      eventData.form = {
        errorType: botEntry.errorMessage
      };
      trackAdobeAction({
        action: AdobeAnalyticsAction.NEW_PAGE,
        pageData: stripNilProps(eventData)
      });
      return;
    }

    trackAdobeAction({
      action: AdobeAnalyticsAction.GEN_AI_CONTENT_UPDATED,
      pageData: stripNilProps(eventData)
    });

  };

  const trackAdobeStart = (
    _props: {
      botEntry: ChatEntryDto;
      userEntry: ChatEntryDto;
    }
  ) => {

    const {
      botEntry,
      userEntry,
    } = _props;

    const eventData: Partial<AdobeAnalyticsPageData> = {
      form: null,
      genAI: {
        conversationId: botEntry.chatId.toString(),
        promptCounter: getChatEntryCount(botEntry),
        input: userEntry.message,
        inputSource: 'user',
        inputOrigin: 'chat'
      },
    };

    trackAdobeAction({
      action: AdobeAnalyticsAction.GEN_AI_CONTENT_REQUESTED,
      pageData: stripNilProps(eventData)
    });

  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  // Returns the submit chain so callers (and tests) can observe it settling. Nothing in the app
  // consumes the return value, so this does not change behaviour -- in particular the chain still
  // carries no catch(), leaving rejections to the existing error handling.
  const handleHttpStreamSubmit = (entrySubmitProps: EntrySubmitProps): Promise<void> => {

    const {
      prompt,
      followUpQuestion,
      isNewChat,
      isAppLinkQuery,
      isPromptExample,
      chatMode
    } = entrySubmitProps;

    return getActiveOrCreateChat(isNewChat, prompt)
      .then(({ chat, wasCreated }) => {
        const tempUserEntry = buildTempUserEntry(
          { ...appState, chatModeType: chatMode },
          state,
          chat,
          prompt,
          followUpQuestion,
        );
        const dehydratedChatEntryDto: ShellsDto = {
          message: tempUserEntry.entry.message,
          filters: tempUserEntry.entry.filters,
          context: tempUserEntry.context,
          attributes: tempUserEntry.entry.attributes,
          iframeContext: tempUserEntry.iframeContext,
        };

        // Include admin search override if enabled
        if (isAdminOverrideEnabled() && state.searchOverride) {
          dehydratedChatEntryDto.searchOverride = state.searchOverride;
        }
        if (isAdminOverrideEnabled() && state.promptOverride) {
          dehydratedChatEntryDto.promptOverride = state.promptOverride;
        }

        // A chat that was just created already has the real title set (see getActiveOrCreateChat),
        // so its first entry only needs the local lastAccessed bump. A *reused* chat's first entry
        // still needs the title PUT -- it may be an old empty "New chat" that never got one.
        const chatUpdate = wasCreated || tempUserEntry.entry.index !== 0
          ? updateChatOnLaterPrompt(tempUserEntry.entry, chat)
          : handleChatUpdateAction({
            appState,
            appStateRef,
            setAppState,
            chat: {
              ...chat,
              title: tempUserEntry.entry.message.substring(0, 1000)
            }
          });

        return chatUpdate.then(() => {
          return saveAndSetShells(dehydratedChatEntryDto, chat.id);
        });
      })
      .then((response) => {
        const [userEntry, botEntry] = response;

        if (isIframeContext(appState.appLinkData) && window.parent) {
          window.parent.postMessage({ type: IframeEvent.SHERPATH_AI_PROMPT_SUBMITTED }, '*');
        }

        trackNewRelicAction({
          appStateRef,
          actionProps: {
            action: NewRelicAnalyticsAction.AI_CHAT_PROMPT_SUBMITTED,
            props: {
              selectedIsbn: getSelectedIsbnFilters(appState.selectedIsbnMap).join(','),
              entryId: userEntry.id,
              idx: userEntry.index,
              isAppLinkQuery,
              isFollowUpQuestion: !!followUpQuestion,
              isPromptExample,
              chatUserRole: appState.chatUserRole,
            }
          }
        });

        setState((prevState) => {
          return {
            ...prevState,
            prompt: '',
            streamStart: moment().toISOString(),
            firstTokenReceived: null,
          };
        });

        // Return focus to input after submission for better keyboard navigation
        const inputElement = document.getElementById('chat-prompt-input') as HTMLTextAreaElement;
        if (inputElement) {
          inputElement.focus();
        }

        trackAdobeStart({ userEntry, botEntry });

        const streamPromise = botEntry.entryType === ChatEntryTypeDto.MESSAGE
          ? Promise.resolve(botEntry)
          : httpStream(userEntry, botEntry)
            .then(() => {
              setState((prevState) => {
                return {
                  ...prevState,
                  chatEntries: prevState.chatEntries.map((entry: ChatEntryDto): ChatEntryDto => {
                    if (entry.id === botEntry.id) {
                      return {
                        ...entry,
                        _status: ChatEntryStatus.POST_PROCESSING // This is FE only prop used to show the loading spinner on citations
                      };
                    }
                    return entry;
                  })
                };
              });
              return handlePostProcessing(botEntry, 0, chatMode);
            })

        return streamPromise.then((postProcessedEntry) => {

          if (postProcessedEntry.errorMessage) {
            trackNewRelicAction({
              appStateRef,
              actionProps: {
                action: NewRelicAnalyticsAction.AI_CHAT_ERROR,
                props: {
                  type: postProcessedEntry.errorMessage,
                  entryId: postProcessedEntry.id,
                  chatId: postProcessedEntry.chatId,
                }
              }
            });
          }

          setState((prevState) => {
            return {
              ...prevState,
              chatEntries: resolveEntriesAfterPostProcess(prevState.chatEntries, botEntry, postProcessedEntry),
              screenReaderAnnouncementStatus: ScreenReaderAnnouncementStatus.COMPLETE,
            };
          });
          trackResponseComplete({ botEntry, userEntry });
          // Clear announcement after a short delay
          screenReaderAnnouncementTimeout.current = setTimeout(() => {
            setState((prevState) => ({
              ...prevState,
              screenReaderAnnouncementStatus: ScreenReaderAnnouncementStatus.IDLE,
            }));
            screenReaderAnnouncementTimeout.current = null;
          }, 3000);
        });
      })
      .finally(() => {
        // Clear the optimistic user entry. Doing it in finally() covers the failure path too --
        // if chat creation or the entry save rejects, the unsaved message must not stay on screen.
        // This deliberately stays a finally() rather than a catch() so existing rejection handling
        // still fires.
        setState((prevState) => {
          return {
            ...prevState,
            tempUserEntry: null,
          };
        });

        // Read off history.location rather than the closed-over `location` -- this finally() runs
        // after async work, and by then a query launch may already have stripped landOnNewChat via
        // its own history.replace (see the isAppLinkQuery effect above). Rebuilding the URL from the
        // stale `location.search` captured at submit time would resurrect that param. Stripping both
        // params here (rather than just isAppLinkQuery) also ensures a plain landOnNewChat launch
        // (no query) gets it removed once the submit settles, instead of leaving it for a later
        // effect run to consume against a possibly-changed activeChatId.
        const currentLocation = history.location;
        const updatedURL: string = deleteSearchParams(currentLocation.pathname, getObjectFromSearchParams(currentLocation.search), ['isAppLinkQuery', 'landOnNewChat']);
        // Only update URL if it actually changed to avoid triggering VoiceOver page announcements
        if (updatedURL !== currentLocation.pathname + currentLocation.search) {
          history.replace(updatedURL);
        }
        setAppState((prevState) => {
          return {
            ...prevState,
            isLoading: false,
          };
        });
        // Don't reset screenReaderAnnouncementStatus here - it's managed in the .then() block
        // to ensure COMPLETE announcement is properly delivered before clearing to IDLE
      });
  };

  // Optional followUpQuestion parameter enables immediate send on button click
  // which is ultimately used in buildTempUserEntry
  // Returns the submit chain so callers (and tests) can await it. Every caller ignores the return
  // value, so this is not a behaviour change; the blocked-input path resolves immediately.
  const handleChatSubmit = (entrySubmitProps: EntrySubmitProps): Promise<void> => {

    if (isInputBlocked(appState)) {
      toastActiveStreamingError({
        appState,
        appStateRef,
        setAppState,
        toastService: props.toastService,
        trackNewRelicAction,
        actionRef: `SUBMIT_${ChatConnectionProtocol.HTTP_STREAM}`
      });
      return Promise.resolve();
    }

    setAppState((prevState) => {
      return {
        ...prevState,
        isLoading: true,
      }
    })

    // Render the user's own message straight away rather than after the submit chain's round trips.
    // For a first-ever submit the chat does not exist yet, so this display-only copy is built against
    // whatever chat context exists (possibly none). It is always replaced by the persisted entries --
    // or removed, if the submit fails -- before anything reads its chatId, and the request payload is
    // built separately inside handleHttpStreamSubmit from the real chat.
    const tempUserEntry = buildTempUserEntry(
      { ...appState, chatModeType: entrySubmitProps.chatMode },
      state,
      getActiveChat(appState) ?? ({ id: null } as ChatDto),
      entrySubmitProps.prompt,
      entrySubmitProps.followUpQuestion,
    ).entry;

    setState((prevState) => {
      return {
        ...prevState,
        tempUserEntry,
        submitStart: moment().toISOString(),
        isAutoScrolling: true,
        scrollToChatBottom: true,
      };
    });

    if (entrySubmitProps.followUpQuestion) {
      patchFollowUpQuestionAction({
        appState,
        setAppState,
        appStateRef,
        followUpQuestionId: entrySubmitProps.followUpQuestion.id,
      });
    }

    // Clear any pending screen reader announcement timeout to ensure proper announcement cycle
    if (screenReaderAnnouncementTimeout.current) {
      clearTimeout(screenReaderAnnouncementTimeout.current);
      screenReaderAnnouncementTimeout.current = null;
    }

    // Reset to IDLE first to ensure the next COMPLETE announcement is detected as a change
    setState((prevState) => ({
      ...prevState,
      screenReaderAnnouncementStatus: ScreenReaderAnnouncementStatus.IDLE,
    }));

    // Delaying screen reader announcement to prevent interruption of other aria-live announcements
    // (button clicks, focus changes, follow-up questions, etc.)
    setTimeout(() => {
      setState((prevState) => {
        return {
          ...prevState,
          screenReaderAnnouncementStatus: ScreenReaderAnnouncementStatus.LOADING
        };
      });
    }, 1000);

    return handleHttpStreamSubmit(entrySubmitProps);
  };

  useEffect(() => {

    if (!appState.entitlements || !appState.chats) {
      return;
    }

    const isAppLinkQuery = ELSURLHelper.getParameterByName('isAppLinkQuery', location.search);

    if (
      isAppLinkQuery === 'true'
      && appState.appLinkData?.outPostBody?.query
    ) {
      if (state.appLinkQuerySubmitted) {
        return;
      }

      // The query takes precedence over landOnNewChat (spec decision), but this branch returns
      // before the landOnNewChat block below ever runs, so that param would otherwise never get
      // consumed. Strip it here too so a later re-run of this effect (e.g. once chats updates)
      // doesn't fall through to the landOnNewChat check and unexpectedly clear the user's
      // in-session active chat using a stale flag from this launch.
      if (ELSURLHelper.getParameterByName('landOnNewChat', location.search) === 'true') {
        const updatedURL: string = deleteSearchParams(RoutePath.AI_CHAT, getObjectFromSearchParams(location.search), ['landOnNewChat']);
        if (updatedURL !== location.pathname + location.search) {
          history.replace(updatedURL);
        }
      }

      handleChatSubmit({
        prompt: appState.appLinkData.outPostBody.query,
        followUpQuestion: null,
        isNewChat: true,
        isAppLinkQuery: true,
        isPromptExample: false,
        chatMode: appStateRef.current.chatModeType
      });
      setState((prevState) => {
        return {
          ...prevState,
          appLinkQuerySubmitted: true
        };
      });
      return;
    }

    // Products can opt to land on the New Chat page instead of resuming their most recent chat.
    // The launching app-link puts landOnNewChat on the URL and it is read here, rather than on
    // mount, because this effect does no real work until chats and entitlements have loaded --
    // reading it earlier would consume the param before the landing decision is actually made.
    //
    // This sits *above* the activeChatId guard because AppProvider hydrates the whole appState
    // (activeChatId included) from session storage, so a landOnNewChat launch can arrive with a
    // stored chat already selected; the landing has to actively clear it, not just skip selection.
    // The param is stripped as soon as it is consumed, so later re-runs of this effect fall
    // through to the activeChatId guard below and a chat the user picks in-session stays picked.
    const landOnNewChat = ELSURLHelper.getParameterByName('landOnNewChat', location.search);

    if (landOnNewChat === 'true') {
      const updatedURL: string = deleteSearchParams(RoutePath.AI_CHAT, getObjectFromSearchParams(location.search), ['landOnNewChat']);
      // Only update URL if it actually changed to avoid triggering VoiceOver page announcements
      if (updatedURL !== location.pathname + location.search) {
        history.replace(updatedURL);
      }

      setAppState((prevState) => {
        return {
          ...prevState,
          activeChatId: null
        }
      });
      return;
    }

    if (appState.activeChatId) {
      return;
    }

    setAppState((prevState) => {
      const activeChat = getDefaultActiveChat(prevState, prevState.activeChatId);
      return {
        ...prevState,
        activeChatId: activeChat ? activeChat.id : null
      }
    })

  }, [appState.chats, appState.entitlements, state.appLinkQuerySubmitted]);

  useEffect(() => {
    const handleUserMessageSubmission = (e: MessageEvent) => {
      if (!isAllowedMessageOrigin(e.origin)) {
        return;
      }

      if (!e.data || e.data.type !== IframeEvent.SHERPATH_AI_SUBMIT_USER_MESSAGE) {
        return;
      }

      const { message, mode } = e.data.data || {};
      if (typeof message !== 'string' || message.trim().length === 0) {
        return;
      }

      // chatMode is threaded through to the submit path so this submission uses the
      // new mode immediately instead of the stale appState.chatModeType (setAppState is async).
      const chatMode = resolveRequestedChatMode(mode, appStateRef.current);
      if (chatMode !== appStateRef.current.chatModeType) {
        // The embedder-specified mode is intentionally persisted here (sticky), not applied as
        // a one-shot override: chatModeType is saved to localStorage by AppProvider and restored
        // on reload, matching the agreed product behaviour for this ticket.
        setAppState((prevState) => ({
          ...prevState,
          chatModeType: chatMode
        }));
      }

      handleChatSubmit({
        prompt: message,
        followUpQuestion: null,
        isNewChat: false,
        isAppLinkQuery: false,
        isPromptExample: false,
        chatMode,
      });
    };

    window.addEventListener('message', handleUserMessageSubmission, false);

    return () => {
      window.removeEventListener('message', handleUserMessageSubmission, false);
    };
  }, [handleChatSubmit]);

  useEffect(() => {
    // The URL is authoritative: enableAdminOverride tracks ?adminOverride=true and is
    // turned back off when the param is removed (so the admin override UI and overrides
    // don't linger on the screen / in requests after the param is dropped).
    const adminOverrideFromUrl = ELSURLHelper.getParameterByName('adminOverride', location.search);
    setAppState((prevState) => {
      return {
        ...prevState,
        enableAdminOverride: adminOverrideFromUrl === 'true'
      }
    })
  }, [location.search]);

  // Init
  useEffect(() => {
    let adminOverride = null;

    const isAppLinkAdminOverride = ELSURLHelper.getParameterByName('isAppLinkAdminOverride', location.search);

    if (appState?.appLinkData?.outPostBody?.isIframe) {
      setAppState((prevState) => {
        return {
          ...prevState,
          isSidebarOpen: false
        }
      })
    }

    if (isAppLinkAdminOverride === 'true') {
      if (appState.appLinkData?.outPostBody?.adminOverride) {
        ({ adminOverride } = { adminOverride: appState.appLinkData.outPostBody.adminOverride });
      }

      const updatedURL: string = deleteSearchParams(RoutePath.AI_CHAT, getObjectFromSearchParams(location.search), ['isAppLinkAdminOverride']);
      // Only update URL if it actually changed to avoid triggering VoiceOver page announcements
      if (updatedURL !== location.pathname + location.search) {
        history.replace(updatedURL);
      }
    } else {
      adminOverride = getAdminOverrideFromStorage();
    }

    if (adminOverride) {
      setState((prevState) => {
        return {
          ...prevState,
          promptOverride: adminOverride.promptOverride,
          searchOverride: adminOverride.searchOverride,
          adminOverridePageContext: adminOverride.adminOverridePageContext || ''
        };
      });
      putAdminOverrideInStorage(adminOverride);
    }
  }, []);

  const handlePromptChange = (e) => {
    const { value } = e.target;
    setState((prevState) => {
      return {
        ...prevState,
        prompt: value
      };
    });
  };

  const handlePromptSend = (e) => {
    if (!state.prompt || state.prompt.trim() === '') {
      return;
    }
    if ((e.key === 'Enter' && !e.shiftKey) || e.type === 'click') {
      e.preventDefault();
      handleChatSubmit({
        prompt: state.prompt,
        followUpQuestion: null,
        isNewChat: false,
        isAppLinkQuery: false,
        isPromptExample: false,
        chatMode: appStateRef.current.chatModeType
      });
    }
  };

  const loadMoreChatEntries = () => {
    const {
      chatEntries
    } = state;
    const {
      activeChatId
    } = appState;
    setAppState((prevState) => {
      return {
        ...prevState,
        isLoading: true,
      };
    });
    const nextPage = Math.floor(chatEntries.length / pageSize);
    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_LOAD_MORE_ENTRIES_CLICK,
        props: {
          nextPage,
          activeChatId
        }
      }
    });

    fetchChatEntriesAction({
      appState,
      appStateRef,
      setAppState,
      chatId: activeChatId,
      page: nextPage,
      pageSize
    }).then((response) => {
      setState((prevState) => {
        const newEntryIds = response.content.map((entry) => entry.id);
        const updatedInitiallyLoadedEntryIds = new Set([...Array.from(prevState.initiallyLoadedEntryIds), ...newEntryIds]);
        return {
          ...prevState,
          totalPages: response.totalPages,
          currentPage: response.currentPage,
          chatEntries: uniqBy([...response.content, ...prevState.chatEntries], 'id'),
          initiallyLoadedEntryIds: updatedInitiallyLoadedEntryIds,
        };
      });
    }).finally(() => {
      setAppState((prevState) => {
        return {
          ...prevState,
          isLoading: false,
        };
      });
    });
  };

  const onEntryEvaluationSave = (entryEvaluation: ChatEntryEvaluationDto) => {

    trackAdobeAction({
      action: AdobeAnalyticsAction.SURVEY_SUBMISSION,
      pageData: {
        survey: {
          type: 'THUMBS_UP_SURVEY',
          score: entryEvaluation.overall.toString(),
          comment: entryEvaluation.comment,
          question: 'How would you describe this response?',
        }
      }
    });

    setState((prevState) => {
      if (!prevState.chatEvaluations || !prevState.chatEvaluations.length) {
        return {
          ...prevState,
          chatEvaluations: [
            entryEvaluation
          ]
        };
      }
      const existingRecord = prevState.chatEvaluations.find((item) => {
        return item.id === entryEvaluation.id;
      });
      if (existingRecord) {
        return {
          ...prevState,
          chatEvaluations: prevState.chatEvaluations.map((item) => {
            if (item.id === entryEvaluation.id) {
              return entryEvaluation;
            }
            return item;
          })
        };
      }
      return {
        ...prevState,
        chatEvaluations: [
          ...prevState.chatEvaluations,
          entryEvaluation
        ]
      };
    });

  };

  const handleSaveOverride = (
    promptOverride: PromptOverrideDto,
    searchOverride: SearchOverrideDto,
    adminOverridePageContext: string
  ) => {
    const {
      modalService,
    } = props;

    setState((prevState) => {
      return {
        ...prevState,
        promptOverride,
        searchOverride,
        adminOverridePageContext
      };
    });
    putAdminOverrideInStorage({ promptOverride, searchOverride, adminOverridePageContext });
    modalService.closeModal(ADMIN_MODAL_ID);
  };

  const handleOpenAdminModal = () => {
    const {
      modalService,
    } = props;
    modalService.openCustomModal({
      modalId: ADMIN_MODAL_ID,
      dialogAriaLabel: 'Admin Overrides',
      noFocusTrap: true,
      modal: (
        <AiChatAdminModal
          promptOverride={state.promptOverride}
          searchOverride={state.searchOverride}
          adminOverridePageContext={state.adminOverridePageContext}
          handleSave={handleSaveOverride}
          handleCancel={() => modalService.closeModal(ADMIN_MODAL_ID)}
        />
      )
    });
  };

  const getLiveAnnouncement = (): string => {
    const { screenReaderAnnouncementStatus } = state;

    switch (screenReaderAnnouncementStatus) {
      case ScreenReaderAnnouncementStatus.LOADING:
        return `${getBrandName(appState)} is generating its response`;
      case ScreenReaderAnnouncementStatus.COMPLETE:
        return `${getBrandName(appState)} has finished generating a response`;
      case ScreenReaderAnnouncementStatus.ERROR:
        return 'An error occurred while generating the response';
      case ScreenReaderAnnouncementStatus.IDLE:
      default:
        return '';
    }
  };

  const getIsShowExplainer = () => {

    const {
      chatEntries,
    } = state;

    const {
      activeChatId
    } = appState;

    // A submit is in flight. The first submit of a brand-new chat chains several requests before
    // any entry lands in state, and none of the values below change in the meantime -- without
    // this the landing view would sit on screen for the whole round trip.
    if (appState.isLoading) {
      return false;
    }

    if (
      !activeChatId
      && !chatEntries
      && appState.chats
      && appState.chats.length === 0
    ) {
      return true;
    }

    // Entries are null before a chat's history loads and whenever no chat is active. Only the
    // former should suppress the explainer -- with no active chat there is nothing to load, so
    // fall through to the same no-active-chat answer the final return gives.
    if (chatEntries === null) {
      return !activeChatId;
    }

    return !(activeChatId && chatEntries?.length);
  };

  const isShowPromptExamples = (): boolean => {
    const isIframe = isIframeContext(appState.appLinkData)
    if (isIframe) {
      return false;
    }

    const context = getChatContextForNewChat(appState)
    if ([
      ChatContextTypeDto.PLP,
      ChatContextTypeDto.IFRAME,
      ChatContextTypeDto.SPARK,
    ].includes(context.contextType)) {
      return false;
    }
    return getIsShowExplainer();
  };

  const updateChatEntryReference = (newReference: ChatReferenceDto) => {
    setState((prevState) => {
      return {
        ...prevState,
        chatEntries: prevState.chatEntries.map((entry) => {
          if (entry.id === newReference.entryId) {
            return {
              ...entry,
              references: (entry.references || []).map((reference) => {
                return reference.id === newReference.id ? newReference : reference;
              })
            }
          }
          return entry;
        })
      };
    })
  }

  const brand = getBrand(appState);

  return (
    <AiChatBaseTemplate
      isPanelOpen={!!state.selectedChatReference || !!state.selectedRelatedContent
        || !!state.selectedFlashcardEntryId || !!state.selectedQuizQuestionEntryId
        || !!state.selectedCitationsEntryId}
      panelContent={(
        <Panel
          chatReference={state.selectedChatReference}
          relatedContent={state.selectedRelatedContent}
          selectedFlashcardEntryId={state.selectedFlashcardEntryId}
          selectedQuizQuestionEntryId={state.selectedQuizQuestionEntryId}
          selectedQuizQuestionMode={state.selectedQuizQuestionMode}
          selectedCitationsEntryId={state.selectedCitationsEntryId}
          selectedCitationIndex={state.selectedCitationIndex}
          citationsEntry={state.selectedCitationsEntryId ? state.chatEntries?.find((e) => e.id === state.selectedCitationsEntryId) : null}
          onCitationClick={(citation: ChatReferenceDto) => {
            const ref = citation as ChatReferenceWithParsedBookContentProps;
            const pageStart = getStartPageNumber(ref) || 1;
            const isClickable = isCitationClickable(appState, ref);

            trackNewRelicAction({
              appStateRef,
              actionProps: {
                action: NewRelicAnalyticsAction.AI_CHAT_CITATION_LINK_CLICK,
                props: {
                  type: ref.type,
                  resourceType: ref.resourceType,
                  citationId: ref.id,
                  entryId: ref.entryId,
                  eeoISBN: ref._contentProps?.source_id,
                  pageStart,
                  isNotLinked: !isClickable,
                  contentType: ref._contentProps?.content_type
                }
              }
            });

            if (!isClickable) {
              return;
            }

            if (isCKSLink(appState, ref)) {
              window.open(buildCksUrlFromCitation(ref), '_blank');
              return;
            }

            if (
              isFeatureFlagEnabled(
                FEATURE_FLAG.ENABLE_VST_EBOOK_LINK_EVENTS,
                appState.featureFlagsGrouped,
                appState.courseSectionId
              )
              && isVSTChat(appState)
            ) {
              const navigatePayload = {
                isbn: ref._contentProps?.isbn,
                startPage: getStartPageNumber(ref),
                endPage: getEndPageNumber(ref),
                chunkId: ref.contentId,
                breadcrumbs: ref._contentProps?.bread_crumbs,
                citationId: ref.id,
                entryId: ref.entryId
              };
              trackNewRelicAction({
                appStateRef,
                actionProps: {
                  action: NewRelicAnalyticsAction.AI_CHAT_CITATION_VST_NAVIGATE,
                  props: {
                    isbn: navigatePayload.isbn,
                    startPage: navigatePayload.startPage,
                    endPage: navigatePayload.endPage,
                    chunkId: navigatePayload.chunkId,
                    breadcrumbTitles: (navigatePayload.breadcrumbs ?? []).map((b) => b.title),
                    citationId: navigatePayload.citationId,
                    entryId: navigatePayload.entryId
                  }
                }
              });
              if (window.parent) {
                window.parent.postMessage({
                  type: IframeEvent.SHERPATH_AI_NAVIGATE_TO_CITATION,
                  ...navigatePayload
                }, '*');
              }
              return;
            }

            const cfiRequest = isFeatureFlag(FEATURE_FLAG.ENABLE_EBOOK_CFI_LINKING)
              ? buildEbookCfiSsoUrlRequestFromCitation({ appState, citation: ref })
              : null;

            openEbookAtCfiOrPageRange({
              appState,
              appStateRef,
              setAppState,
              cfiRequest,
              buildPageRangeRequest: () => buildEbookSsoUrlRequestFromCitation({ appState, citation: ref })
            })
              .catch(() => {
                toastApiError(props.toastService);
              });
          }}
          onClosePanel={() => {
            setState((prevState) => {
              // Determine the thumbnail ID from the currently selected content
              let thumbnailId: string = null;
              if (prevState.selectedChatReference) {
                thumbnailId = `reference-content-${prevState.selectedChatReference.id}`;
              } else if (prevState.selectedRelatedContent) {
                thumbnailId = `related-content-${prevState.selectedRelatedContent.id}`;
              }

              // Scroll back to the clicked thumbnail after a brief delay
              if (thumbnailId) {
                setTimeout(() => {
                  const element = document.getElementById(thumbnailId);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }, 100);
              }

              return {
                ...prevState,
                selectedChatReference: null,
                selectedRelatedContent: null,
                selectedFlashcardEntryId: null,
                selectedQuizQuestionEntryId: null,
                selectedQuizQuestionMode: null,
                selectedCitationsEntryId: null,
              }
            })
          }}
        />
      )}
    >
      <div className="c-els-ai-chat">
        <div className="c-els-ai-chat__center">
          <div className="c-els-ai-chat__width">
            <div
              className="c-els-ai-chat__entries"
              id="chat-scroll"
            >
              {
                getIsShowExplainer() && (
                  <div className="c-els-ai-chat__explainer">
                    <div className="c-els-ai-chat__explainer-content">
                      <h2>
                        {brand === Brand.NORA
                          ? 'Hello!'
                          : `Hello, ${getUserName(appState).firstName}`}
                      </h2>
                      <p className="o-els-container">
                        {getBrandName(appState)} is a conversational tool powered by generative AI and designed to
                        support students and educators by delivering evidence-based responses to healthcare-related questions.
                        It is not a calculator and should not be used to determine medication dosages.
                      </p>
                      <p>
                        <Link to={RoutePath.HOW_IT_WORKS} className="c-els-link c-els-link--inline">
                          View pointers for getting started
                        </Link>
                        <span> or ask away and happy learning!</span>
                      </p>
                    </div>
                  </div>
                )
              }

              {
                state.totalPages > 1 && state.currentPage < state.totalPages - 1 && (
                  <div>
                    <FlexLayout>
                      <FlexItem>
                        <button
                          className="c-els-button c-els-button--small"
                          type="button"
                          onClick={() => {
                            loadMoreChatEntries();
                          }}
                        >
                          Load past messages
                        </button>
                      </FlexItem>
                    </FlexLayout>
                  </div>
                )
              }

              {
                (state.chatEntries || state.tempUserEntry) && (
                  getSortedChatEntries(
                    state.tempUserEntry ? [...(state.chatEntries || []), state.tempUserEntry] : state.chatEntries
                  ).map((chatEntry, entryIndex, array) => {
                    return (
                      <AiChatEntry
                        key={chatEntry.id}
                        entry={chatEntry}
                        parentEntry={getParentEntry(chatEntry, state)}
                        onFeedbackSaveClick={onEntryEvaluationSave}
                        entryEvaluation={getEntryEvaluation(chatEntry, state)}
                        entryIndex={entryIndex}
                        totalEntries={array.length}
                        isAdminFeatureOn={isAdminOverrideEnabled()}
                        followUpQuestions={state.followUpQuestions}
                        handleSubmit={handleChatSubmit}
                        onUpdateChatEntryReference={updateChatEntryReference}
                        onFlashcardClick={(entryId: number) => {
                          setState((prevState) => ({
                            ...prevState,
                            selectedFlashcardEntryId: entryId,
                            selectedChatReference: null,
                            selectedRelatedContent: null,
                            selectedQuizQuestionEntryId: null,
                            selectedQuizQuestionMode: null,
                            selectedCitationsEntryId: null,
                          }));
                        }}
                        selectedFlashcardEntryId={state.selectedFlashcardEntryId}
                        onQuizQuestionClick={(entryId: number, mode: QuizPanelMode) => {
                          trackNewRelicAction({
                            appStateRef,
                            actionProps: {
                              action: NewRelicAnalyticsAction.AI_CHAT_QUIZ_QUESTION_VIEW_CLICK,
                              props: {
                                entryId,
                                mode,
                                chatUserRole: appState.chatUserRole
                              }
                            }
                          });
                          setState((prevState) => ({
                            ...prevState,
                            selectedQuizQuestionEntryId: entryId,
                            selectedQuizQuestionMode: mode,
                            selectedChatReference: null,
                            selectedRelatedContent: null,
                            selectedFlashcardEntryId: null,
                            selectedCitationsEntryId: null,
                          }));
                        }}
                        selectedQuizQuestionEntryId={state.selectedQuizQuestionEntryId}
                        selectedQuizQuestionMode={state.selectedQuizQuestionMode}
                        onCitationsClick={(entryId: number, citationIndex?: number) => {
                          setState((prevState) => ({
                            ...prevState,
                            selectedCitationsEntryId: entryId,
                            selectedCitationIndex: citationIndex ?? null,
                            selectedFlashcardEntryId: null,
                            selectedQuizQuestionEntryId: null,
                            selectedQuizQuestionMode: null,
                            selectedChatReference: null,
                            selectedRelatedContent: null,
                          }));
                        }}
                        isCitationsLoading={isFilterRelevantReferencesJobPending(chatEntry)}
                        isFollowUpQuestionsLoading={(() => {
                          const followUpJob = getPostProcessJob(chatEntry, PostProcessJobTypeDto.GENERATE_FOLLOW_UP_QUESTIONS);
                          // Hide the section entirely until the job arrives — we can't
                          // know yet whether one was dispatched.
                          if (!followUpJob) {
                            return false;
                          }
                          if (isPostProcessJobInProgress(followUpJob)) {
                            return true;
                          }
                          // The job may have just reached COMPLETED but the async
                          // fetchFollowUpQuestionsAction hasn't resolved yet.  Keep the
                          // loading indicator on until that fetch resolves — tracked per
                          // entry so an entry with zero follow-up questions (e.g. a
                          // citation-free response) stops loading instead of spinning forever.
                          if (followUpJob.status === PostProcessJobStatusDto.COMPLETED) {
                            return !state.followUpResolvedEntryIds.has(chatEntry.id);
                          }
                          return false;
                        })()}
                        onRelatedContentClick={(chatReference: ChatReferenceDto) => {
                          trackNewRelicAction({
                            appStateRef,
                            actionProps: {
                              action: NewRelicAnalyticsAction.AI_CHAT_REFERENCE_CLICK,
                              props: {
                                type: chatReference.type,
                                resourceType: chatReference.resourceType,
                                referenceId: chatReference.id,
                                entryId: chatReference.entryId
                              }
                            }
                          });

                          setAppState((prevState) => {
                            return {
                              ...prevState,
                              isPanelOpen: true
                            }
                          });
                          setState((prevState) => {
                            return {
                              ...prevState,
                              selectedChatReference: chatReference,
                              selectedFlashcardEntryId: null,
                              selectedRelatedContent: null,
                              selectedCitationsEntryId: null,
                            };
                          });
                        }}
                        onNewRelatedContentClick={(relatedContent: RelatedContentDto) => {
                          trackNewRelicAction({
                            appStateRef,
                            actionProps: {
                              action: NewRelicAnalyticsAction.AI_CHAT_RELATED_CONTENT_CLICK,
                              props: {
                                contentType: relatedContent.contentType,
                                relatedContentId: relatedContent.id,
                                entryId: relatedContent.entryId,
                              }
                            }
                          });

                          setAppState((prevState) => {
                            return {
                              ...prevState,
                              isPanelOpen: true
                            }
                          });
                          setState((prevState) => {
                            return {
                              ...prevState,
                              selectedRelatedContent: relatedContent,
                              selectedFlashcardEntryId: null,
                              selectedChatReference: null,
                              selectedCitationsEntryId: null,
                            };
                          });
                        }}
                        isFromInitialLoad={state.initiallyLoadedEntryIds.has(chatEntry.id)}
                        isIframe={isIframeContext(appState.appLinkData)}
                      />
                    );
                  })
                )
              }
            </div>
          </div>
        </div>
        <AiChatInputBar
          prompt={state.prompt}
          isShowPromptExamples={isShowPromptExamples()}
          chatEntries={state.chatEntries}
          handlePromptChange={handlePromptChange}
          handlePromptSend={handlePromptSend}
          handleOpenAdminModal={handleOpenAdminModal}
          handleChatSubmit={handleChatSubmit}
          modalService={props.modalService}
        />
        <span
          className={getObjectFromSearchParams(location.search).loga11y ? 'u-naa-a11y-debug' : 'u-els-hide-visually'}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {getLiveAnnouncement()}
        </span>
      </div>
    </AiChatBaseTemplate>
  );
};
// withPageLoader must come after connect
const enhance = flowRight(
  ELSWithToastService,
  ELSWithModalService,
  withHTMLHeadSEO({ title: 'Chat' }),
  withPageLoader,
);

const AiChat = enhance(AiChatComponent) as React.ComponentType<typeof emptyObject>;
export default AiChat;
